import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule, DecimalPipe } from '@angular/common';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatListModule } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  CsvImporterModule,
  InlineHelpModule,
  NavDropdownModule,
  ToastsModule,
  WebToolsDropdownModule,
} from '@tmx/operandi';
import { GtagModule } from 'angular-gtag';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { ModalModule } from 'ngx-bootstrap/modal';
import { TimepickerModule } from 'ngx-bootstrap/timepicker';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { APP_USES_LOGIN } from 'src/environments/app-uses-login';
import { AuthModule } from '../app/auth/auth.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AlertService } from './auth/services/alert.service';
import { AuthenticationService } from './auth/services/authentication.service';
import { AddFavoriteModalComponent } from './components/add-favorite-modal/add-favorite-modal.component';
import { ChatSupportComponent } from './components/chat-support/chat-support.component';
import { CustomPlaceComponent } from './components/custom-place/custom-place.component';
import { FavoritePlaceResultComponent } from './components/favorite-place/favorite-place-result/favorite-place-result.component';
import { FavoritePlaceComponent } from './components/favorite-place/favorite-place.component';
import { FooterComponent } from './components/footer/footer.component';
import { LoginAlertComponent } from './components/login-alert/login-alert.component';
import { MapAddStopMarkerComponent } from './components/map/map-addstop-marker/map-addstop-marker.component';
import { MapControlsComponent } from './components/map/map-controls/map-controls.component';
import { NextPrevViewComponent } from './components/map/map-controls/next-prev-view/next-prev-view.component';
import { MapRouteComponent } from './components/map/map-route/map-route.component';
import { MapRoutesComponent } from './components/map/map-routes/map-routes.component';
import { MapComponent } from './components/map/map.component';
import { ModalboxComponent } from './components/modalbox/modalbox.component';
import { RouteImportModalComponent } from './components/route-panel/route-import-modal/route-import-modal.component';
import { RoutePanelComponent } from './components/route-panel/route-panel.component';
import { RoutePanelArriveDepartComponent } from './components/route-panel/route/arrive-depart/arrive-depart.component';
import { ImportStopsComponent } from './components/route-panel/route/import-stops/import-stops.component';
import { RouteOptimizationComponent } from './components/route-panel/route/route-optimization/route-optimization.component';
import { RoutePanelProfileListComponent } from './components/route-panel/route/route-profile/profile-list.component';
import { RouteComponent } from './components/route-panel/route/route.component';
import { DeleteRouteComponent } from './components/route-panel/saved-route/delete-route.component';
import { OpenRouteComponent } from './components/route-panel/saved-route/open-route.component';
import { RouteDialogLocationComponent } from './components/route-panel/saved-route/route-dialog-location.component';
import { SaveRouteComponent } from './components/route-panel/saved-route/save-route.component';
import { UpdateRouteComponent } from './components/route-panel/saved-route/update-route.component';
import { StopItemComponent } from './components/route-panel/stop-item/stop-item.component';
import { StopListComponent } from './components/route-panel/stop-list/stop-list.component';
import { SearchResultComponent } from './components/search/search-result/search-result.component';
import { SearchComponent } from './components/search/search.component';
import { TestComponent } from './components/test/test.component';
import { WebToolsMenuComponent } from './components/web-tools-menu/web-tools-menu.component';
import { LandingComponent } from './landing/landing.component';
import { ReportsModule } from './reports/reports.module';
import { ConversionService } from './services/conversion.service';
import { LocationMarkerService } from './services/location-marker.service';
import { LocationService } from './services/location.service';
import { MapService } from './services/map.service';
import { PlaceSetService } from './services/place-set.service';
import { SearchService } from './services/search.service';
import { SpinnerService } from './services/spinner.service';
import { TripService } from './services/trip.service';
import { RoutingProfileService } from './services/vehicle-routing-profile.service';
import { SharedModule } from './shared-module/shared.module';
import { configureDataAccessEnvironment } from './utils/configure-data-access-environment';
import { AuthGuard } from './_guards/auth.guard';
import { PromoAuthGuard } from './_guards/promo-auth.guard';
import { FullAddressFilterPipe } from './_helpers/full-address-filter.pipe';
import { HexToRGBaPipe } from './_helpers/hex-to-rgba.pipe';
import { initCookieLogin } from './_helpers/init-cookie-login';
import { RequestHandlingInterceptor } from './_helpers/request.handling.interceptor';
import { StopDurationPipe } from './_helpers/stop-duration-helper';
import { StopHoursPipe } from './_helpers/stop-hours-pipe';
import { DataVersionListComponent } from './components/route-panel/data-version-list/data-version-list.component';
import { TimeFieldComponent } from './components/route-panel/route/time-field/time-field.component';
import { OpenLegacyRouteComponent } from './components/route-panel/saved-route/open-legacy-route.component';
import { SearchByAddressPipe } from './reports/_helpers/search-pipe';
import { PrecisionPipe } from './_helpers/precision-pipe';
@NgModule({
  declarations: [
    AppComponent,
    TestComponent,
    SearchComponent,
    SearchResultComponent,
    MapComponent,
    MapControlsComponent,
    ModalboxComponent,
    RouteComponent,
    MapAddStopMarkerComponent,
    RoutePanelComponent,
    MapRoutesComponent,
    MapRouteComponent,
    StopListComponent,
    StopItemComponent,
    WebToolsMenuComponent,
    FooterComponent,
    HexToRGBaPipe,
    NextPrevViewComponent,
    FavoritePlaceComponent,
    FavoritePlaceResultComponent,
    RoutePanelProfileListComponent,
    RoutePanelArriveDepartComponent,
    ChatSupportComponent,
    OpenRouteComponent,
    OpenLegacyRouteComponent,
    FullAddressFilterPipe,
    RouteDialogLocationComponent,
    DeleteRouteComponent,
    CustomPlaceComponent,
    LoginAlertComponent,
    SaveRouteComponent,
    UpdateRouteComponent,
    AddFavoriteModalComponent,
    StopDurationPipe,
    PrecisionPipe,
    StopHoursPipe,
    SearchByAddressPipe,
    RouteOptimizationComponent,
    LandingComponent,
    ImportStopsComponent,
    RouteImportModalComponent,
    DataVersionListComponent,
    TimeFieldComponent,
  ],
  imports: [
    CommonModule,
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    DragDropModule,
    TypeaheadModule.forRoot(),
    GtagModule.forRoot({ trackingId: 'G-WG52SHH4TW', trackPageviews: true }),
    AuthModule,
    MatListModule,
    MatButtonModule,
    MatSelectModule,
    MatDialogModule,
    TooltipModule.forRoot(),
    PopoverModule.forRoot(),
    BsDropdownModule.forRoot(),
    BsDatepickerModule.forRoot(),
    TimepickerModule.forRoot(),
    ModalModule.forRoot(),
    ReportsModule,
    SharedModule,
    InlineHelpModule,
    ToastsModule,
    CsvImporterModule,
    NavDropdownModule,
    WebToolsDropdownModule,
  ],
  providers: [
    LocationService,
    LocationMarkerService,
    MapService,
    AuthGuard,
    AlertService,
    ConversionService,
    SearchService,
    SpinnerService,
    DecimalPipe,
    PlaceSetService,
    RoutingProfileService,
    TripService,
    PromoAuthGuard,
    {
      provide: APP_INITIALIZER,
      useFactory: configureDataAccessEnvironment,
      multi: true,
    },
    { provide: APP_USES_LOGIN, useValue: true },
    {
      provide: APP_INITIALIZER,
      useFactory: initCookieLogin,
      deps: [AuthenticationService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RequestHandlingInterceptor,
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
